import React, { useState } from "react";
import { tvandinternet, fiberinternet, corporateinternet } from "../Static";
import { FiWifi, FiTv, FiActivity, FiCheck, FiInfo, FiZap, FiPhone, FiSliders } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

export default function TariffsSection() {
  const { t, language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState("bundle"); // "bundle" | "fiber" | "corporate"

  const getLocalizedTitle = (title) => {
    if (language === "en") {
      const map = {
        "Կարմիր": "Red",
        "Կապույտ": "Blue",
        "Ծիրանագույն": "Orange",
        "Ա+": "A+",
        "Բ+": "B+",
      };
      return map[title] || title;
    }
    if (language === "ru") {
      const map = {
        "Կարմիր": "Красный",
        "Կապույտ": "Синий",
        "Ծիրանագույն": "Оранжевый",
        "Ա+": "А+",
        "Բ+": "Б+",
      };
      return map[title] || title;
    }
    return title;
  };

  const renderCard = (item, isBundle = false) => {
    const speed = parseInt(item.speed, 10);
    const normallyAvailable = Math.round(speed * 0.7 * 10) / 10;
    const minimumSpeed = Math.round(speed * 0.2 * 10) / 10;
    const isPopular = item.title === "Կապույտ" || item.title === "Ultra";

    return (
      <div
        key={item.id}
        className={`relative flex flex-col justify-between rounded-3xl p-6 md:p-8 transition-all duration-300 ${
          isPopular
            ? "bg-white border-2 border-brandRed shadow-glow-red scale-[1.02] z-10"
            : "bg-white border border-slate-100 shadow-premium hover:shadow-premium-hover hover:border-slate-200"
        }`}
      >
        {isPopular && (
          <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2 bg-brandRed text-white text-[11px] font-black uppercase tracking-widest px-4 py-1 rounded-full shadow-sm flex items-center space-x-1">
            <FiZap size={12} />
            <span>{t("tariffs.popularBadge")}</span>
          </div>
        )}

        <div>
          {/* Card Header */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-5">
            <div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest block">
                {isBundle ? "CATV + NET" : item.cat === "corporate" ? t("tariffs.tabCorporate") : "Fiber"}
              </span>
              <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mt-0.5">
                {getLocalizedTitle(item.title)}
              </h3>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-brandRed flex items-center justify-center flex-shrink-0">
              {isBundle ? <FiTv size={24} /> : <FiWifi size={24} />}
            </div>
          </div>

          {/* Pricing */}
          <div className="pt-5 pb-6">
            <div className="flex items-baseline space-x-1.5">
              <span className="text-5xl font-black text-slate-900 font-bebas tracking-tight">
                {parseInt(item.price, 10).toLocaleString()}
              </span>
              <span className="text-base font-bold text-slate-400">{t("tariffs.currency")}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{t("tariffs.noHiddenFees")}</p>
          </div>

          {/* Specs */}
          <div className="space-y-3.5 pt-2 border-t border-slate-100">
            {/* Speed Spec */}
            <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-100/80">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-xl bg-white text-brandRed shadow-sm flex items-center justify-center">
                  <FiActivity size={16} />
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase leading-none">
                    {t("tariffs.speedLabel")}
                  </div>
                  <div className="text-xs font-semibold text-slate-700 mt-0.5">
                    {t("tariffs.speedUpTo")}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-2xl font-black text-brandRed font-bebas leading-none">
                  {item.speed}
                </span>
                <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">
                  {t("tariffs.speedUnit")}
                </span>
              </div>
            </div>

            {/* TV Channels Spec if bundle */}
            {isBundle && (
              <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-100/80">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 rounded-xl bg-white text-brandRed shadow-sm flex items-center justify-center">
                    <FiTv size={16} />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase leading-none">
                      {t("tariffs.channelsLabel")}
                    </div>
                    <div className="text-xs font-semibold text-slate-700 mt-0.5">
                      {t("tariffs.channelsSub")}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-black text-slate-800 font-bebas leading-none">
                    {item.channels}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">
                    {t("tariffs.channelsUnit")}
                  </span>
                </div>
              </div>
            )}

            {/* Feature Checklist */}
            <div className="space-y-2 pt-2">
              <div className="flex items-center space-x-2 text-xs font-medium text-slate-700">
                <FiCheck className="text-emerald-500 flex-shrink-0" size={16} />
                <span>{t("tariffs.freeWifi")}</span>
              </div>
              <div className="flex items-center space-x-2 text-xs font-medium text-slate-700">
                <FiCheck className="text-emerald-500 flex-shrink-0" size={16} />
                <span>{t("tariffs.techMonitoring")}</span>
              </div>
              <div className="flex items-center space-x-2 text-xs font-medium text-slate-700">
                <FiCheck className="text-emerald-500 flex-shrink-0" size={16} />
                <span>{t("tariffs.unlimitedTraffic")}</span>
              </div>
            </div>

            {/* Speed transparency box */}
            <div className="p-3 bg-amber-50/60 rounded-2xl border border-amber-100/80 text-[11px] text-amber-900 space-y-1">
              <div className="flex items-center space-x-1.5 font-bold uppercase tracking-wider text-[10px] text-amber-800">
                <FiInfo size={13} />
                <span>{t("tariffs.transparencyTitle")}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>{t("tariffs.normallyAvailable")}</span>
                <span className="font-bold text-slate-900">{normallyAvailable} {t("tariffs.speedUnit")}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>{t("tariffs.minimumSpeed")}</span>
                <span className="font-bold text-slate-900">{minimumSpeed} {t("tariffs.speedUnit")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Footer: Direct Connection Call */}
        <div className="pt-6">
          <a
            href="tel:+37460666999"
            className={`w-full py-3 px-4 rounded-2xl font-bold uppercase tracking-wider text-xs flex items-center justify-center space-x-2 transition-all duration-200 cursor-pointer ${
              isPopular
                ? "bg-brandRed hover:bg-brandRedHover text-white shadow-glow-red"
                : "bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-200"
            }`}
          >
            <FiPhone size={13} className={isPopular ? "text-white" : "text-brandRed"} />
            <span>{t("tariffs.callButton")}</span>
          </a>
        </div>
      </div>
    );
  };

  return (
    <section id="tariffs" className="w-full py-20 bg-slate-50/60 border-t border-slate-100">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Heading */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-100 text-brandRed text-xs font-bold uppercase tracking-wider mb-3">
            <FiZap size={13} />
            <span>{t("tariffs.badge")}</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight uppercase">
            {t("tariffs.title")}
          </h2>
          <p className="text-slate-600 text-sm md:text-base mt-3 leading-relaxed">
            {t("tariffs.subtitle")}
          </p>

          {/* Segmented Controller Tabs */}
          <div className="mt-8 inline-flex p-1.5 bg-slate-200/80 rounded-2xl border border-slate-200">
            <button
              onClick={() => setActiveCategory("bundle")}
              className={`py-2.5 px-4 md:px-6 rounded-xl text-xs md:text-sm font-bold uppercase tracking-wider transition-all duration-200 flex items-center space-x-2 cursor-pointer ${
                activeCategory === "bundle"
                  ? "bg-white text-brandRed shadow-md"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <FiTv size={15} />
              <span>{t("tariffs.tabBundle")}</span>
            </button>
            <button
              onClick={() => setActiveCategory("fiber")}
              className={`py-2.5 px-4 md:px-6 rounded-xl text-xs md:text-sm font-bold uppercase tracking-wider transition-all duration-200 flex items-center space-x-2 cursor-pointer ${
                activeCategory === "fiber"
                  ? "bg-white text-brandRed shadow-md"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <FiWifi size={15} />
              <span>{t("tariffs.tabFiber")}</span>
            </button>
            <button
              onClick={() => setActiveCategory("corporate")}
              className={`py-2.5 px-4 md:px-6 rounded-xl text-xs md:text-sm font-bold uppercase tracking-wider transition-all duration-200 flex items-center space-x-2 cursor-pointer ${
                activeCategory === "corporate"
                  ? "bg-white text-brandRed shadow-md"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <FiActivity size={15} />
              <span>{t("tariffs.tabCorporate")}</span>
            </button>
          </div>
        </div>

        {/* Tariffs Grid */}
        <div className="w-full">
          {activeCategory === "bundle" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
              {tvandinternet.map((t) => renderCard(t, true))}
            </div>
          )}

          {activeCategory === "fiber" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
              {fiberinternet.map((t) => renderCard(t, false))}
            </div>
          )}

          {activeCategory === "corporate" && (
            <div className="grid grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto gap-8 items-stretch">
              {corporateinternet.map((t) => renderCard(t, false))}
            </div>
          )}
        </div>

        {/* Assistance strip */}
        <div className="mt-14 p-6 bg-white border border-slate-100 rounded-3xl shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3 text-center sm:text-left">
            <div className="w-10 h-10 rounded-2xl bg-red-50 text-brandRed flex items-center justify-center flex-shrink-0">
              <FiInfo size={20} />
            </div>
            <div>
              <h4 className="text-sm font-extrabold text-slate-800 uppercase">
                {t("tariffs.helpTitle")}
              </h4>
              <p className="text-xs text-slate-500">
                {t("tariffs.helpSubtitle")}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              const el = document.getElementById("calculator") || document.getElementById("speedtest");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="px-6 py-3 rounded-2xl bg-brandRed hover:bg-brandRedHover text-white font-bold text-xs uppercase tracking-wider shadow-glow-red hover:shadow-glow-red-lg transition-all duration-200 flex-shrink-0 flex items-center space-x-2 cursor-pointer"
          >
            <FiSliders size={14} />
            <span>{t("tariffs.helpButton")}</span>
          </button>
        </div>
      </div>
    </section>
  );
}
